import React, { useState, useEffect } from "react";
import SocialShare from "../utils/SocialShare";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import "../../styles/Article.css";
import SubscriptionComponent from "../utils/SubscriptionComponent";
import SEO from "../utils/SEO";
import WebPImage from "../utils/WebPImage";
import { trackArticleRead } from "../../utils/analytics";
import { parseArticleSplat, buildArticlePath } from "../../utils/articleUrl";

const ArticleComponent = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Clean URL: /learn/article/<slug>/<id>  -> params["*"] = "blogs/recipes/5"
  const parsed = parseArticleSplat(params["*"]);

  // Legacy URL: /learn/article?topic=/data/x.json&articleId=1 (kept working so
  // links shared before the clean-URL migration still resolve).
  const legacyQuery = new URLSearchParams(location.search);
  const legacyTopic = legacyQuery.get("topic");
  const legacyId = legacyQuery.get("articleId")
    ? parseInt(legacyQuery.get("articleId"), 10)
    : null;

  const topic = parsed ? parsed.topicFile : legacyTopic;
  const articleId = parsed ? parsed.articleId : legacyId;
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  // Redirect legacy query-param links to the canonical clean URL.
  useEffect(() => {
    if (!parsed && legacyTopic && legacyId != null && !Number.isNaN(legacyId)) {
      navigate(buildArticlePath(legacyTopic, legacyId), { replace: true });
    }
  }, [parsed, legacyTopic, legacyId, navigate]);

  useEffect(() => {
    if (topic) {
      fetch(topic.startsWith("/data/") ? topic : `/data/${topic}`)
        .then((response) => response.json())
        .then((data) => {
          const foundArticle = data.find((art) => art.id === articleId);
          setArticle(foundArticle);
          // Sibling articles from the same topic file -> "Related" internal links.
          setRelated(
            Array.isArray(data)
              ? data
                  .filter((a) => a && a.id != null && a.title && a.id !== articleId)
                  .slice(0, 4)
              : []
          );
          if (foundArticle) {
            trackArticleRead(foundArticle.title, topic);
          }
        })
        .catch((error) => console.error("Error fetching article:", error));
    }
  }, [topic, articleId]);

  const renderContent = (content) => {
    // The opening paragraph gets a larger "lead" treatment (editorial style).
    const firstParagraphIndex = content.findIndex(
      (item) => item.type === "paragraph"
    );
    return content.map((item, index) => {
      switch (item.type) {
        case "header":
          return (
            <h2 key={index} className="article-page__h2">
              {item.text}
            </h2>
          );
        case "paragraph":
          return (
            <p
              key={index}
              className={
                index === firstParagraphIndex
                  ? "article-page__lead"
                  : "article-page__p"
              }
            >
              {item.text}
            </p>
          );
        case "image":
          return (
            <WebPImage
              key={index}
              src={`${process.env.PUBLIC_URL}/${item.src}`}
              alt={item.alt}
              className="article-page__inline-img"
              loading="lazy"
            />
          );
        case "list":
          return (
            <ul key={index} className="article-page__list">
              {item.items.map((listItem, listItemIndex) => (
                <li key={listItemIndex}>{listItem}</li>
              ))}
            </ul>
          );
        case "reference":
          return (
            <section key={index} className="article-page__sources">
              <h4 className="article-page__sources-title">Sources</h4>
              <ul>
                {item.references.map((reference, refIndex) => (
                  <li key={refIndex}>{reference}</li>
                ))}
              </ul>
            </section>
          );
        default:
          return null;
      }
    });
  };

  if (!article) {
    return <div className="article-page__loading">Loading…</div>;
  }

  const baseUrl = "https://joinsarv.com";
  const articlePath = buildArticlePath(topic, articleId);
  const articleUrl = `${baseUrl}${articlePath}`;
  const articleImageUrl = article.img
    ? `${baseUrl}/${article.img}`
    : `${baseUrl}/img/og-image.jpg`;

  const articleText =
    article.text ||
    (article.content
      ? article.content
          .filter((item) => item.type === "paragraph")
          .slice(0, 2)
          .map((item) => item.text)
          .join(" ")
          .substring(0, 160)
      : "");

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: articleText,
    image: articleImageUrl,
    author: {
      "@type": "Organization",
      name: "Sarv Health Team",
      url: baseUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Sarv",
      url: baseUrl,
      logo: {
        "@type": "ImageObject",
        url: `${baseUrl}/v.png`,
      },
    },
    dateModified: article.lastUpdate || new Date().toISOString(),
    url: articleUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": articleUrl,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
      {
        "@type": "ListItem",
        position: 2,
        name: "Library",
        item: `${baseUrl}/learn`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - Sarv Health Library`}
        description={articleText.substring(0, 155) + "..."}
        canonicalPath={articlePath}
        image={articleImageUrl}
        type="article"
        articleData={{
          publishedTime: article.lastUpdate,
          section: "Health & Wellness",
        }}
        keywords={`${article.title}, medicinal herbs, natural remedies, holistic health, herbal medicine, personalized wellness`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <article className="article-page">
        <nav className="article-page__breadcrumb" aria-label="breadcrumb">
          <Link to="/">Home</Link>
          <span className="article-page__breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <Link to="/learn">Library</Link>
          <span className="article-page__breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <span className="article-page__breadcrumb-current">
            {article.title}
          </span>
        </nav>

        <div className="article-page__meta">
          <span className="article-page__kicker">Health &amp; Wellness</span>
          {article.lastUpdate && (
            <span className="article-page__updated">
              Updated {article.lastUpdate}
            </span>
          )}
        </div>

        <h1 className="article-page__title">{article.title}</h1>

        {article.img && (
          <figure className="article-page__hero-wrap">
            <WebPImage
              src={`${process.env.PUBLIC_URL}/${article.img}`}
              alt={article.title}
              className="article-page__hero"
              loading="eager"
            />
          </figure>
        )}

        <div className="article-page__body">
          {article.content && renderContent(article.content)}
        </div>

        {/* In-article CTA — primary conversion hook (the dosha quiz). */}
        <aside className="article-page__cta">
          <p>
            Curious which natural remedies fit your body type?{" "}
            <Link to="/quiz">Take the free Ayurvedic dosha quiz →</Link>
          </p>
        </aside>

        <SocialShare />

        {related.length > 0 && (
          <section className="article-page__related">
            <h2>Related articles</h2>
            <ul>
              {related.map((a) => (
                <li key={a.id}>
                  <Link to={buildArticlePath(topic, a.id)}>{a.title}</Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>

      <SubscriptionComponent />
    </>
  );
};

export default ArticleComponent;
